import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Form, Button, Alert } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { usersAPI } from '../../services/usersApi'

const schema = yup.object({
  email: yup.string().email('Некорректный email').required('Email обязателен'),
  password: yup.string().when('$isEdit', {
    is: false,
    then: (schema) => schema.required('Пароль обязателен').min(6, 'Минимум 6 символов'),
    otherwise: (schema) => schema.min(6, 'Минимум 6 символов'),
  }),
  password_confirmation: yup.string().oneOf([yup.ref('password')], 'Пароли не совпадают'),
  role: yup.string().required('Выберите роль'),
}).required()

const UserForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [user, setUser] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit },
  })

  useEffect(() => {
    if (isEdit) {
      loadUser()
    }
  }, [id])

  const loadUser = async () => {
    try {
      setLoading(true)
      const response = await usersAPI.get(id)
      setUser(response.data)
      reset({
        email: response.data.email,
        role: response.data.roles?.[0] || '',
      })
    } catch (err) {
      setError('Ошибка загрузки пользователя')
      console.error('Error loading user:', err)
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      setError(null)

      if (isEdit) {
        await usersAPI.update(id, data)
      } else {
        await usersAPI.create(data)
      }

      navigate('/users')
    } catch (err) {
      if (err.response?.data?.errors) {
        const errorMessages = Object.entries(err.response.data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('\n')
        setError(errorMessages)
      } else {
        setError(isEdit ? 'Ошибка при обновлении пользователя' : 'Ошибка при создании пользователя')
      }
      console.error('Error saving user:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <h1 className="mb-4">
        {isEdit ? 'Редактировать пользователя' : 'Создать нового пользователя'}
      </h1>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <h5>Ошибки:</h5>
          <pre className="mb-0">{error}</pre>
        </Alert>
      )}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            {...register('email')}
            isInvalid={!!errors.email}
          />
          <Form.Control.Feedback type="invalid">
            {errors.email?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Пароль {isEdit && '(оставьте пустым, чтобы не менять)'}</Form.Label>
          <Form.Control
            type="password"
            {...register('password')}
            isInvalid={!!errors.password}
          />
          <Form.Control.Feedback type="invalid">
            {errors.password?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Подтверждение пароля</Form.Label>
          <Form.Control
            type="password"
            {...register('password_confirmation')}
            isInvalid={!!errors.password_confirmation}
          />
          <Form.Control.Feedback type="invalid">
            {errors.password_confirmation?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Роль</Form.Label>
          <Form.Select {...register('role')} isInvalid={!!errors.role}>
            <option value="">Выберите роль</option>
            <option value="foreman">Прораб</option>
            <option value="admin">Админ</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {errors.role?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <div className="mb-3">
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Сохранение...' : isEdit ? 'Обновить пользователя' : 'Создать пользователя'}
          </Button>
          <Link to="/users" className="btn btn-secondary ms-2">
            Отмена
          </Link>
        </div>
      </Form>
    </div>
  )
}

export default UserForm
